import { Col, Row } from 'antd';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Pause,
  Play,
  Replay,
  ReplayOne,
  ShuffleIcon,
  SkipBack,
  SkipNext,
  PreviousRecordingIcon,
  NextRecordingIcon
} from '../../../Icons';
import { usePlayback } from '../../../../contexts/PlaybackContext';
import { deriveQueuePointer } from '../../../../lib/playback/queueDisplay';
import type { QueueItem } from '../../../../lib/queue/types';

const deriveRecordingKey = (item: QueueItem | undefined, fallback: string): string => {
  if (!item) {
    return fallback;
  }
  if (
    item.groupType === 'recording' &&
    typeof item.groupId === 'string' &&
    item.groupId.trim().length > 0
  ) {
    return item.groupId.trim();
  }
  const metadata = item.metadata ?? {};
  const rawRecordingId = metadata['recordingId'];
  if (typeof rawRecordingId === 'string' && rawRecordingId.trim().length > 0) {
    return rawRecordingId.trim();
  }
  if (typeof item.groupId === 'string' && item.groupId.trim().length > 0) {
    return item.groupId.trim();
  }
  return item.id ?? fallback;
};

const findPreviousRecordingStartIndex = (
  items: QueueItem[],
  pointerIndex: number | null
): number | null => {
  if (pointerIndex == null || pointerIndex <= 0 || pointerIndex >= items.length) {
    return null;
  }

  const currentKey = deriveRecordingKey(items[pointerIndex], `queue-index-${pointerIndex}`);
  for (let index = pointerIndex - 1; index >= 0; index -= 1) {
    const candidateKey = deriveRecordingKey(items[index], `queue-index-${index}`);
    if (candidateKey !== currentKey) {
      let start = index;
      while (start - 1 >= 0) {
        const prevKey = deriveRecordingKey(items[start - 1], `queue-index-${start - 1}`);
        if (prevKey !== candidateKey) {
          break;
        }
        start -= 1;
      }
      return start;
    }
  }
  return null;
};

const findNextRecordingStartIndex = (
  items: QueueItem[],
  pointerIndex: number | null
): number | null => {
  if (pointerIndex == null || pointerIndex < 0 || pointerIndex >= items.length - 1) {
    return null;
  }

  const currentKey = deriveRecordingKey(items[pointerIndex], `queue-index-${pointerIndex}`);
  for (let index = pointerIndex + 1; index < items.length; index += 1) {
    const candidateKey = deriveRecordingKey(items[index], `queue-index-${index}`);
    if (candidateKey !== currentKey) {
      return index;
    }
  }
  return null;
};

const ControlButtons = () => {
  const {
    status,
    playerState,
    isPlaying,
    isPlayingDisplay,
    pauseTrack,
    resumeTrack,
    nextTrack,
    previousTrack,
    skipRecording,
    toggleShuffle,
    setRepeatMode,
    queueSession,
    queueItems,
    queueStatus,
    performQueueAction
  } = usePlayback();

  const repeatMode = playerState?.repeat_mode ?? 0;

  const pointer = useMemo(() => deriveQueuePointer(queueSession, queueItems), [queueItems, queueSession]);
  const previousRecordingIndex = useMemo(
    () => findPreviousRecordingStartIndex(queueItems, pointer.index),
    [queueItems, pointer.index]
  );
  const nextRecordingIndex = useMemo(
    () => findNextRecordingStartIndex(queueItems, pointer.index),
    [queueItems, pointer.index]
  );

  const queueReady = status === 'ready' && queueStatus === 'ready' && !!queueSession;
  const playerReady = status === 'ready'; // Player can be ready even if queue isn't loaded yet
  const positionMs =
    typeof playerState?.position === 'number' && Number.isFinite(playerState.position)
      ? playerState.position
      : 0;
  const canSkipPrev = queueReady && pointer.index != null && pointer.index > 0;
  // Allow restart of the current track even if there is no previous item.
  const canRestartCurrent = queueReady && positionMs > 5000;
  const canPrev = canSkipPrev || canRestartCurrent;
  const canSkipNext =
    queueReady && pointer.index != null && pointer.index >= 0 && pointer.index < queueItems.length - 1;
  // Playback controls should work as soon as player is ready, not wait for queue
  const canPauseOrResume = playerReady;
  const shuffleActive = queueSession?.shuffleEnabled ?? false;
  const canSkipPreviousRecording = queueReady && previousRecordingIndex != null;
  const canSkipNextRecording = queueReady && nextRecordingIndex != null;

  const [optimisticPlay, setOptimisticPlay] = useState<{
    target: boolean;
    setAt: number;
    seq: number;
  } | null>(null);
  const optimisticSeqRef = useRef(0);
  const optimisticTimerRef = useRef<number | null>(null);
  const logPrefix = '[PlayPauseOptimistic]';

  const handlePlayPause = async () => {
    const currentDisplay = optimisticPlay ? optimisticPlay.target : isPlayingDisplay;
    const nextSeq = optimisticSeqRef.current + 1;
    optimisticSeqRef.current = nextSeq;
    const target = !currentDisplay;
    if (optimisticTimerRef.current != null) {
      clearTimeout(optimisticTimerRef.current);
      optimisticTimerRef.current = null;
    }
    const timerId = window.setTimeout(() => {
      optimisticTimerRef.current = null;
      console.log(logPrefix, 'set optimistic (delay)', {
        target,
        at: Date.now(),
        isPlayingDisplay,
        currentDisplay,
        seq: nextSeq,
      });
      setOptimisticPlay({ target, setAt: Date.now(), seq: nextSeq });
    }, 100);
    optimisticTimerRef.current = timerId;
    if (!canPauseOrResume) return;
    console.log(logPrefix, isPlaying ? 'pauseTrack start' : 'resumeTrack start', {
      isPlaying,
      isPlayingDisplay,
      currentDisplay,
      target,
      seq: nextSeq,
    });
    if (isPlaying) {
      await pauseTrack();
    } else {
      await resumeTrack();
    }
    console.log(logPrefix, isPlaying ? 'pauseTrack done' : 'resumeTrack done', {
      isPlaying,
      isPlayingDisplay,
      target,
      seq: nextSeq,
    });
    if (optimisticTimerRef.current === timerId) {
      clearTimeout(optimisticTimerRef.current);
      optimisticTimerRef.current = null;
    }
    setOptimisticPlay((prev) => (prev && prev.seq === nextSeq ? null : prev));
  };

  useEffect(() => {
    return () => {
      if (optimisticTimerRef.current != null) {
        clearTimeout(optimisticTimerRef.current);
        optimisticTimerRef.current = null;
      }
    };
  }, []);

  const handleRepeatToggle = async () => {
    const nextMode =
      repeatMode === 2 ? 'off' : repeatMode === 1 ? 'track' : 'context';
    await setRepeatMode(nextMode);
  };

  const handlePreviousRecording = useCallback(() => {
    if (!canSkipPreviousRecording || previousRecordingIndex == null) {
      return;
    }
    void performQueueAction('jump-to-index', { targetIndex: previousRecordingIndex }).catch((error) => {
      console.error('Failed to jump to previous recording via queue action:', error);
    });
  }, [canSkipPreviousRecording, performQueueAction, previousRecordingIndex]);

  const handleNextRecording = useCallback(() => {
    if (!canSkipNextRecording) {
      return;
    }
    void skipRecording();
  }, [canSkipNextRecording, skipRecording]);

  return (
    <Row gutter={24} align='middle' style={{ justifyContent: 'center' }}>
      <Col>
        <button onClick={() => toggleShuffle(!shuffleActive)}>
          <ShuffleIcon active={shuffleActive} />
        </button>
      </Col>
      <Col>
        <button
          className={canSkipPreviousRecording ? '' : 'disabled'}
          onClick={handlePreviousRecording}
          aria-label='Previous recording'
          title='Previous recording'
          data-queue-control='previous-recording'
        >
          <PreviousRecordingIcon />
        </button>
      </Col>
      <Col>
        <button
          className={canPrev ? '' : 'disabled'}
          onClick={() => canPrev && previousTrack()}
        >
          <SkipBack />
        </button>
      </Col>
      <Col>
        <button
          className={`player-pause-button ${canPauseOrResume ? '' : 'disabled'}`}
          onClick={handlePlayPause}
        >
          {(optimisticPlay ? optimisticPlay.target : isPlayingDisplay) ? <Pause /> : <Play />}
        </button>
      </Col>
      <Col>
        <button
          className={canSkipNext ? '' : 'disabled'}
          onClick={() => canSkipNext && nextTrack()}
        >
          <SkipNext />
        </button>
      </Col>
      <Col>
        <button
          className={canSkipNextRecording ? '' : 'disabled'}
          onClick={handleNextRecording}
          aria-label='Next recording'
          title='Next recording'
          data-queue-control='skip-recording'
        >
          <NextRecordingIcon />
        </button>
      </Col>
      <Col>
        <button
          className={repeatMode === 2 ? 'active-icon-button' : ''}
          onClick={handleRepeatToggle}
        >
          {repeatMode === 2 ? <ReplayOne active /> : <Replay active={repeatMode !== 0} />}
        </button>
      </Col>
    </Row>
  );
};

export default ControlButtons;
